/**
 * [TD-47.6c-D.1] PGFRadarChart - 5-axis radar for player analytics.
 *
 * Renders Availability / Productivity / Opportunity / Match Up / Stability
 * as a closed pentagon with each axis labeled and scored 0-100 (display
 * units; underlying data is 0.0-1.0 from `pgf_axis_scores.<axis>_value`).
 *
 * Per Spec §5.1 line 372 - small variant feeds the Canvas v2 modal; the
 * E.3 Cockpit mode uses a compact variant via the `compact` prop.
 *
 * Per Analytics Narrative Discipline Guard (A.7): axis labels match the
 * founder-locked vocabulary exactly. No "AI score" framing in labels or
 * tooltips.
 *
 * [v1.5 Redline] Recharts does not inherit Tailwind dark: classes through
 * SVG attributes reliably. Resolve theme via `useTheme()` and pass
 * explicit hex values to Recharts so grid + labels read clearly in
 * BOTH day and night modes.
 */
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer } from "recharts";
import { useTheme } from "../../contexts/ThemeContext";

const AXIS_LABELS = {
  availability: "Availability",
  productivity: "Productivity",
  opportunity: "Opportunity",
  matchup: "Match Up",
  stability: "Stability",
};

const AXIS_ORDER = ["availability", "productivity", "opportunity", "matchup", "stability"];

export function PGFRadarChart({ axes, compact = false }) {
  const { isDark } = useTheme();
  // Explicit, theme-resolved palette - Recharts gets hex values directly.
  const gridStroke = isDark ? "#4B5563" : "#9CA3AF";
  const labelFill = isDark ? "#FFFFFF" : "#111111";

  if (!axes || typeof axes !== "object") {
    return (
      <div
        data-testid="pgf-radar-empty"
        className="flex items-center justify-center text-xs text-neutral-500 italic"
        style={{ minHeight: compact ? 120 : 240 }}
      >
        Radar data pending
      </div>
    );
  }

  const data = AXIS_ORDER.map((axis) => ({
    axis: AXIS_LABELS[axis],
    value: Math.round((axes[axis] || 0) * 100),
  }));

  const height = compact ? 140 : 320;
  const fontSize = compact ? 10 : 14;

  return (
    <div data-testid="pgf-radar-chart" style={{ width: "100%", height }}>
      <ResponsiveContainer>
        <RadarChart
          data={data}
          outerRadius={compact ? "60%" : "70%"}
          margin={{ top: 8, right: 24, bottom: 8, left: 24 }}
        >
          <PolarGrid
            stroke={gridStroke}
            strokeOpacity={1}
            strokeWidth={1.5}
          />
          <PolarAngleAxis
            dataKey="axis"
            tick={{ fontSize, fill: labelFill, fontWeight: 900, fillOpacity: 1 }}
          />
          {/* [Redline #3 + #7] Hide the radius axis entirely. */}
          <PolarRadiusAxis
            angle={90}
            domain={[0, 100]}
            axisLine={false}
            tick={false}
            tickCount={5}
          />
          <Radar
            name="PGF Rank axis"
            dataKey="value"
            stroke="rgb(37, 99, 235)"
            strokeWidth={2}
            fill="rgb(59, 130, 246)"
            fillOpacity={0.55}
            isAnimationActive={true}
            animationDuration={1200}
            animationEasing="ease-out"
            animationBegin={0}
          />
        </RadarChart>
      </ResponsiveContainer>
    </div>
  );
}

export default PGFRadarChart;
